"use strict";
/**
 * OH Hub API Utility
 *
 * Centralized utility for making requests to the OH Hub API with proper headers
 * including the required Ocp-Apim-Subscription-Key for Azure API Management.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.hubApiDelete = exports.hubApiPut = exports.hubApiPost = exports.hubApiGet = exports.hubApiRequest = exports.hubApiClient = exports.HubApiClient = void 0;
const DEFAULT_TIMEOUT_MS = 15000;
class HubApiClient {
    constructor() {
        this.baseUrl = process.env.NEXT_PUBLIC_HUB_API_BASE_URL || '';
        this.subscriptionKey = process.env.HUB_API_KEY || '';
    }
    validateEnvironment() {
        if (!this.baseUrl) {
            throw new Error('NEXT_PUBLIC_HUB_API_BASE_URL environment variable is required');
        }
        if (!this.subscriptionKey) {
            throw new Error('HUB_API_KEY environment variable is required');
        }
    }
    /**
     * Make a request to the Hub API with proper headers
     */
    async request(endpoint, options = {}) {
        // Validate environment variables at runtime
        this.validateEnvironment();
        const { method = 'GET', headers = {}, body, cache, timeoutMs = DEFAULT_TIMEOUT_MS } = options;
        // Ensure endpoint starts with /
        const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        const url = `${this.baseUrl}${normalizedEndpoint}`;
        const requestHeaders = {
            'Content-Type': 'application/json',
            'Ocp-Apim-Subscription-Key': this.subscriptionKey,
            ...headers,
        };
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        const fetchOptions = {
            method,
            headers: requestHeaders,
            cache,
            signal: controller.signal,
        };
        if (body && method !== 'GET' && method !== 'HEAD') {
            fetchOptions.body = typeof body === 'string' ? body : JSON.stringify(body);
        }
        try {
            const response = await fetch(url, fetchOptions);
            if (!response.ok) {
                throw new Error(`Hub API request failed: ${response.status} ${response.statusText}`);
            }
            const data = await response.json();
            return data;
        }
        catch (error) {
            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new Error(`Hub API request timed out after ${timeoutMs}ms: ${url}`);
                }
                throw error;
            }
            throw new Error('Unknown error occurred while making Hub API request');
        }
        finally {
            clearTimeout(timer);
        }
    }
    /**
     * Convenience method for GET requests
     */
    async get(endpoint, options = {}) {
        return this.request(endpoint, { ...options, method: 'GET' });
    }
    /**
     * Convenience method for POST requests
     */
    async post(endpoint, body, options = {}) {
        return this.request(endpoint, { ...options, method: 'POST', body });
    }
    /**
     * Convenience method for PUT requests
     */
    async put(endpoint, body, options = {}) {
        return this.request(endpoint, { ...options, method: 'PUT', body });
    }
    /**
     * Convenience method for DELETE requests
     */
    async delete(endpoint, options = {}) {
        return this.request(endpoint, { ...options, method: 'DELETE' });
    }
}
exports.HubApiClient = HubApiClient;
// Create a singleton instance for convenience
exports.hubApiClient = new HubApiClient();
// Convenience functions that use the singleton
const hubApiRequest = (endpoint, options) => exports.hubApiClient.request(endpoint, options);
exports.hubApiRequest = hubApiRequest;
const hubApiGet = (endpoint, options) => exports.hubApiClient.get(endpoint, options);
exports.hubApiGet = hubApiGet;
const hubApiPost = (endpoint, body, options) => exports.hubApiClient.post(endpoint, body, options);
exports.hubApiPost = hubApiPost;
const hubApiPut = (endpoint, body, options) => exports.hubApiClient.put(endpoint, body, options);
exports.hubApiPut = hubApiPut;
const hubApiDelete = (endpoint, options) => exports.hubApiClient.delete(endpoint, options);
exports.hubApiDelete = hubApiDelete;
